import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import APPCONSTANTS, { APP_TYPE_NAME, RED_RISK_USER, rolesGroup, suiteAccess } from '../../constants/appConstants';
import localStorageService from '../../global/localStorageServices';
import sessionStorageServices from '../../global/sessionStorageServices';
import * as userService from '../../services/userAPI';
import { routeToParentApp } from '../../utils/routeUtils';
import { info } from '../../utils/toastCenter';
import * as regionActions from '../region/actions';
import { AppState } from '../rootReducer';
import * as userActions from './actions';
import * as USERTYPES from './actionTypes';
import {
  IFetchCHWUsersRequest,
  IFetchLoggedInUserRequest,
  IFetchPHUCHWUsersRequest,
  IFetchRolesListRequest,
  IRoles,
  IUser
} from './types';

export function* logout(): SagaIterator {
  try {
    yield call(userService.logout);
    sessionStorageServices.clearAllItem();
    yield put(userActions.logoutSuccess());
    yield put(userActions.resetStore());
    routeToParentApp(true);
  } catch (e) {
    sessionStorageServices.deleteItem(APPCONSTANTS.AUTHTOKEN);
    yield put(userActions.logoutFailure());
  }
}

export function* fetchTimezoneList(): SagaIterator {
  try {
    const {
      data: { data: timezoneList }
    } = yield call(userService.fetchTimezoneList);
    yield put(userActions.fetchTimezoneListSuccess(timezoneList));
  } catch (e) {
    yield put(userActions.fetchTimezoneListFailure());
  }
}

export function* fetchLoggedInUser(action: IFetchLoggedInUserRequest): SagaIterator {
  try {
    const {
      data: {
        entity: {
          username: email,
          firstName,
          lastName,
          id: userId,
          reportPrivileges,
          roles,
          appTypes,
          tenantId,
          country,
          organizations,
          suiteAccess: newSuiteAccess,
          reportUserOrganization
        }
      }
    } = yield call(userService.fetchLoggedInUser);
    const isReportSuperAdmin = roles.some(
      (role: { name: string; suiteAccessName: string }) =>
        role.name === APPCONSTANTS.ROLES.REPORT_SUPER_ADMIN && role.suiteAccessName === APPCONSTANTS.SUITE_ACCESS.CFR
    );
    // don't put country if logged-in user is isReportSuperAdmin
    if (country && !isReportSuperAdmin) {
      yield put(regionActions.setRegionDetail(country));
      sessionStorageServices.setItem(APPCONSTANTS.REGION_ID, country.id);
      if (country.name.toLowerCase() === APPCONSTANTS.KENYA) {
        yield put(userActions.setIsKenya(true));
        sessionStorageServices.setItem(APPCONSTANTS.IS_KENYA, true);
      }
    }
    sessionStorageServices.setItem(APPCONSTANTS.USER_TENANTID, tenantId);
    sessionStorageServices.setItem(APPCONSTANTS.COUNTRY_TENANT_ID, country?.tenantId);
    const isHF4User = (roles || []).some((role: IRoles) => role.name === APPCONSTANTS.ROLES.HF4_REPORT_USER);
    yield put(userActions.setHF4UserStatus(isHF4User));
    if (reportPrivileges?.length) {
      yield put(userActions.setReportPrivileges(reportPrivileges));
    }

    const filteredRoles = roles.filter((role: any) => Object.values(APPCONSTANTS.ROLES).includes(role.name));
    if (!filteredRoles.length) {
      info(APPCONSTANTS.ALERT, APPCONSTANTS.UNAUTHORIZED_ACCESS_MESSAGE);
      yield put(userActions.logoutRequest());
    }
    const oldAppTypes = yield select((state: AppState) => state.user?.user?.appTypes);
    const payload: IUser = {
      email,
      firstName,
      lastName,
      userId,
      role: filteredRoles[0].name,
      roleDetail: filteredRoles[0],
      isHF4User,
      reportPrivileges,
      tenantId,
      country: isReportSuperAdmin ? null : country,
      suiteAccess: newSuiteAccess,
      formDataId: organizations[0]?.formDataId,
      assignedFacility: organizations[0] || {},
      reportUserOrganization,
      appTypes: isReportSuperAdmin && !oldAppTypes ? [] : appTypes || country?.appTypes || oldAppTypes || []
    };
    if ((payload.appTypes || []).length) {
      localStorageService.setItem(APP_TYPE_NAME, `${JSON.stringify(payload.appTypes)}`);
    }
    yield put(userActions.fetchLoggedInUserSuccess(payload));
  } catch (e) {
    yield put(userActions.fetchLoggedInUserFail());
  }
}

export function* fetchDefaultRoles(): SagaIterator {
  try {
    const { data: roles } = yield call(userService.getDefaultRoles);
    yield put(userActions.fetchDefaultRoleSuccess(roles));
  } catch (e) {
    yield put(userActions.fetchDefaultRoleFailure());
  }
}

export function* fetchCHWUserSaga({
  skip,
  limit,
  tenantId,
  searchTerm,
  isDHISStatusNeeded,
  month,
  year,
  successCb,
  failureCb
}: IFetchCHWUsersRequest): SagaIterator {
  try {
    const {
      data: { entityList: userLists, totalCount }
    } = yield call(userService.fetchCHWUsersAPI, {
      skip,
      limit,
      tenantId,
      searchTerm,
      isDHISStatusNeeded,
      month,
      year
    });
    yield put(userActions.fetchCHWUsersSuccess(userLists, totalCount));
    successCb?.(userLists);
  } catch (e) {
    failureCb?.();
    yield put(userActions.fetchCHWUsersFailure());
  }
}
export function* fetchPHUCHWUserSaga({
  skip,
  limit,
  tenantId,
  searchTerm,
  isDHISStatusNeeded,
  hf4User,
  month,
  year,
  successCb,
  failureCb
}: IFetchPHUCHWUsersRequest): SagaIterator {
  try {
    const {
      data: { entityList: userLists, totalCount }
    } = yield call(userService.fetchPHUCHWUsersAPI, {
      skip,
      limit,
      tenantId,
      searchTerm,
      isDHISStatusNeeded,
      hf4User,
      month,
      year
    });
    yield put(userActions.fetchPHUCHWUsersSuccess(userLists, totalCount));
    successCb?.(userLists);
  } catch (e) {
    failureCb?.();
    yield put(userActions.fetchPHUCHWUsersFailure());
  }
}

export function* fetchRolesListSaga({ countryId, successCB, failureCB }: IFetchRolesListRequest): SagaIterator {
  try {
    const {
      data: { entity: rolesList = [] }
    } = yield call(userService.fetchRolesList, { countryId });
    const mobRoles = (rolesList[rolesGroup.SPICE] || []).filter(
      (role: { suiteAccessName: string; name: string }) =>
        role.suiteAccessName === suiteAccess.mob && role.name !== RED_RISK_USER
    );
    yield put(userActions.fetchRolesListSuccess());
    successCB(mobRoles);
  } catch (e) {
    failureCB(e as Error);
    yield put(userActions.fetchRolesListFailure());
  }
}

/*
  Starts worker saga on latest dispatched `LOGIN_REQUEST` action.
  Allows concurrent increments.
*/
export default function* userSaga(): SagaIterator {
  yield all([
    takeLatest(USERTYPES.LOGOUT_REQUEST, logout),
    takeLatest(USERTYPES.FETCH_TIMEZONE_LIST_REQUEST, fetchTimezoneList),
    takeLatest(USERTYPES.FETCH_LOGGED_IN_USER_REQUEST, fetchLoggedInUser),
    takeLatest(USERTYPES.FETCH_DEFAULT_ROLE, fetchDefaultRoles),
    takeLatest(USERTYPES.FETCH_CHW_USER_LIST_REQUEST, fetchCHWUserSaga),
    takeLatest(USERTYPES.FETCH_PHU_CHW_USER_LIST_REQUEST, fetchPHUCHWUserSaga),
    takeLatest(USERTYPES.FETCH_ROLES_LIST_REQUEST, fetchRolesListSaga)
  ]);
}
